import React, { useEffect, useRef, useState } from 'react'
import { Button, Popover, theme } from 'antd'
import { DownOutlined, FilterOutlined } from '@ant-design/icons'
import { FilterConfig, FilterTrigger } from '../filter-trigger'
import { FilterDrawer, FilterRowLabels, DEFAULT_LABELS } from './filter-drawer'

export type { FilterRowLabels }
export { DEFAULT_LABELS, FilterDrawer }
export type { FilterDrawerProps } from './filter-drawer'
export { useFilterRowLabels } from './use-filter-row-labels'
export { buildLabelsFilterRowItem } from './labels-config'

export type FilterValues = Record<string, string[]>

/**
 * One filter in the row: its config plus the per-filter presentation extras FilterTrigger
 * understands. `clearable: false` also excludes the filter from the row's own Clear all -
 * a filter that must always hold a value (Status: Active) is neither "active" nor clearable.
 */
export interface FilterRowItem extends FilterConfig {
  lockReason?: string
  disabled?: boolean
  quiet?: boolean
  clearable?: boolean
  /** Accessible name for this filter's clear ×. English default - pass a translation. */
  clearLabel?: string
  /** Rich empty-state override for this filter's dropdown (e.g. a "No labels created yet" link
   * to a KB article). Falls back to the row's generic "No data" text when omitted. */
  emptyContent?: React.ReactNode
}

export interface FilterTriggerRowProps {
  /** Ordered - the row renders triggers in array order; the first `visibleLimit` stay visible. */
  configs: FilterRowItem[]
  values: FilterValues
  onChange: (values: FilterValues) => void
  /** Live result count for the mobile drawer footer. Omitted → the footer reads a static
   * Apply label instead of a stale number. */
  resultCount?: number
  /** Fires (debounced 300ms) as the drawer's staged state changes, so the caller can compute
   * resultCount. The component itself never fetches. */
  onStagedChange?: (staged: FilterValues) => void
  /** English defaults - pass translations at the call site (the DataTable convention). */
  labels?: Partial<FilterRowLabels>
  /** How many triggers stay inline before the rest fold behind More.
   * @default VISIBLE_TRIGGER_LIMIT */
  visibleLimit?: number
  /** false keeps the inline triggers at every width instead of collapsing to the drawer under
   * 768px - for hosts whose change handlers can't absorb a staged multi-filter apply yet.
   * @default true */
  collapseOnMobile?: boolean
  /** Overrides the row's own Clear all visibility (e.g. a page whose "active" definition
   * includes state outside the row, like a search box). */
  showClearAll?: boolean
  /** Overrides the row's own Clear all handler. */
  onClearAll?: () => void
}

export const VISIBLE_TRIGGER_LIMIT = 4
// 768 is the supported floor, so the sheet has to appear AT 768, not below it - at 767 the
// collapse could never fire inside the range it was built for.
export const MOBILE_QUERY = '(max-width: 768px)'

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(MOBILE_QUERY).matches : false))
  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY)
    const onChange = (event: MediaQueryListEvent) => setMobile(event.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])
  return mobile
}

export function activeFilterCount(configs: FilterConfig[], values: FilterValues): number {
  return configs.filter((config) => (values[config.id] ?? []).length > 0).length
}

const clearableConfigs = (configs: FilterRowItem[]): FilterRowItem[] => configs.filter((config) => config.clearable !== false)

/**
 * The filter row: a horizontal line of FilterTriggers above a table. Desktop shows the first
 * four configs and folds the rest behind a More popover (count-badged when any hidden filter is
 * active - the spec's one sanctioned badge); Clear all renders directly after the last
 * trigger only while something is active - it acts on the pills, so it sits with them rather
 * than flushed to the container edge - and selections apply immediately. Under
 * 768px the row collapses to a single "Filters · N" button opening the staged-apply drawer.
 */
export function FilterTriggerRow(props: FilterTriggerRowProps) {
  const { configs, values, onChange, resultCount, onStagedChange, labels, visibleLimit, collapseOnMobile, showClearAll, onClearAll } = props
  const { token } = theme.useToken()
  const isMobile = useIsMobile() && collapseOnMobile !== false
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const moreBodyRef = useRef<HTMLDivElement | null>(null)

  const text: FilterRowLabels = { ...DEFAULT_LABELS, ...labels }
  // A filter that must always hold a value (clearable: false) never counts as "active" - its
  // permanent value would otherwise pin the mobile badge and Clear all on forever.
  const activeCount = activeFilterCount(clearableConfigs(configs), values)
  const clearVisible = showClearAll ?? activeCount > 0
  const clearAll = onClearAll ?? (() => onChange({ ...values, ...Object.fromEntries(clearableConfigs(configs).map((config) => [config.id, []])) }))

  if (isMobile) {
    // A disabled filter cannot render as an inert section of toggle chips, so it stays out of
    // the sheet entirely - same reason a locked-but-interactive one stays in.
    const drawerConfigs = configs.filter((config) => !config.disabled)
    return (
      <>
        <Button icon={<FilterOutlined />} onClick={() => setDrawerOpen(true)}>
          {activeCount > 0 ? `${text.filters} · ${activeCount}` : text.filters}
        </Button>
        <FilterDrawer
          open={drawerOpen}
          configs={drawerConfigs}
          applied={values}
          resultCount={resultCount}
          onStagedChange={onStagedChange}
          labels={text}
          onApply={(next) => {
            onChange(next)
            setDrawerOpen(false)
          }}
          onClose={() => setDrawerOpen(false)}
        />
      </>
    )
  }

  const limit = visibleLimit ?? VISIBLE_TRIGGER_LIMIT
  const visible = configs.slice(0, limit)
  const overflow = configs.slice(limit)
  const hiddenActive = activeFilterCount(overflow, values)

  const triggerFor = (config: FilterRowItem, popupContainer?: (trigger: HTMLElement) => HTMLElement) => (
    <FilterTrigger
      key={config.id}
      config={config}
      value={values[config.id] ?? []}
      onChange={(value) => onChange({ ...values, [config.id]: value })}
      popupContainer={popupContainer}
      lockReason={config.lockReason}
      disabled={config.disabled}
      quiet={config.quiet}
      clearable={config.clearable}
      clearLabel={config.clearLabel}
      emptyLabel={text.noOptions}
      emptyContent={config.emptyContent}
      searchLabel={text.search}
      noResultsLabel={text.noResults}
      expandGroupLabel={text.expandGroup}
      collapseGroupLabel={text.collapseGroup}
    />
  )

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: token.paddingXS, flexWrap: 'wrap' }}>
      <style>{`
        .vt-filter-more:focus-visible {
          outline: 2px solid ${token.colorPrimary};
          outline-offset: 2px;
        }
      `}</style>
      {visible.map((config) => triggerFor(config))}

      {overflow.length > 0 && (
        <Popover
          open={moreOpen}
          onOpenChange={setMoreOpen}
          trigger="click"
          placement="bottomLeft"
          arrow={false}
          content={
            // Nested trigger panels portal into this body (popupContainer), so a click inside
            // one never reads as "outside the More popover" and closes it.
            <div ref={moreBodyRef} style={{ display: 'flex', flexDirection: 'column', gap: token.paddingXS, minWidth: 220 }}>
              {overflow.map((config) => triggerFor(config, () => moreBodyRef.current ?? document.body))}
            </div>
          }
          styles={{ body: { padding: token.paddingXS } }}
        >
          <button
            type="button"
            className="vt-filter-more"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: token.paddingXXS,
              borderRadius: token.borderRadius,
              border: `1px solid ${token.colorBorder}`,
              background: token.colorBgContainer,
              color: token.colorText,
              fontSize: token.fontSize,
              lineHeight: `${token.controlHeight}px`,
              padding: `0 ${token.padding}px`,
              cursor: 'pointer',
            }}
          >
            {text.more}
            {hiddenActive > 0 && (
              <span
                aria-label={`${hiddenActive}`}
                style={{
                  minWidth: 18,
                  height: 18,
                  borderRadius: 9,
                  background: token.colorPrimary,
                  color: token.colorTextLightSolid,
                  fontSize: 11,
                  lineHeight: '18px',
                  textAlign: 'center',
                  padding: '0 4px',
                }}
              >
                {hiddenActive}
              </span>
            )}
            <DownOutlined aria-hidden style={{ fontSize: 10 }} />
          </button>
        </Popover>
      )}

      {clearVisible && (
        <Button type="link" size="small" onClick={clearAll}>
          {text.clearAll}
        </Button>
      )}
    </div>
  )
}
